import { type RefObject, useEffect } from 'react';

type UseInputValidationProps = {
  inputRef: RefObject<HTMLInputElement>;
  updateValidity: () => void;
  syncValidity?: () => void;
  validationMessages?: Record<string, string>;
  validatesOnChange?: boolean;
};

/**
 * Custom hook that observes input value changes via MutationObserver
 * to handle validation for autocomplete and other unexpected sources
 */
export const useInputValidation = ({
  inputRef,
  updateValidity,
  syncValidity,
  validationMessages,
  validatesOnChange = true,
}: UseInputValidationProps) => {
  useEffect(() => {
    const node = inputRef.current;
    if (!node) return;

    const handleInputEvent = () => {
      syncValidity?.();

      if (validatesOnChange) {
        updateValidity();
      }
    };
    // Add MutationObserver to watch for changes via autocomplete or any other unexpected source
    const observer = new MutationObserver(handleInputEvent);
    observer.observe(node, { attributes: true, attributeFilter: ['value'] });

    return () => {
      observer.disconnect();
    };
  }, [inputRef, syncValidity, updateValidity, validationMessages, validatesOnChange]);
};
